import { useCallback, useEffect, useState } from 'react';

import { ACTION_GONE, stateFor } from '../recordActionStates';

/**
 * Fetches the record behind one work-section row, for its detail pop-up.
 *
 * A 404 BECOMES `ACTION_GONE` rather than a load error, and that is the whole
 * reason this reads `stateFor` instead of tracking a boolean. The server
 * resolves the record through the same scope that produced the row, so deleted,
 * reassigned, closed and version-unpinned all arrive as one 404 — which is
 * exactly the "no longer available" state the approvals pop-up already renders.
 * One rule, one wording, both pop-ups.
 *
 * NOTHING IS RETURNED UNTIL IT RESOLVES. `detail` stays null while loading and
 * on any failure, so a caller cannot render half a record. There is no React
 * error boundary anywhere in this app and this mounts on the post-login landing
 * page, so a throw here blanks Home entirely.
 *
 * REFETCHES ON `{detailType, id}`, NOT ON THE ROW OBJECT. A row re-read from
 * the server is a new object with the same identity, and depending on the
 * object would refetch on every section refresh.
 */
export default function useWorkDetail({ urlTemplate, detailTypePlaceholder, idPlaceholder, detailType, id }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [state, setState] = useState(null);

    const reset = useCallback(() => {
        setDetail(null);
        setState(null);
    }, []);

    useEffect(() => {
        // Closed pop-up, or a row with no identity: hold nothing rather than
        // keeping the last record around to flash into the next open.
        //
        // `urlTemplate` IS CHECKED, not assumed. It arrives from a `data-*`
        // attribute on the mount element, so a view that mounts Home without
        // publishing it hands this `null` — and calling `.replace` on that
        // throws. There is no error boundary anywhere in this app and this
        // mounts on the post-login landing page, so that throw would blank
        // Home rather than degrade one pop-up.
        if (!urlTemplate || !detailType || !id) {
            setDetail(null);
            setState(null);
            setLoading(false);

            return;
        }

        const url = urlTemplate.replace(detailTypePlaceholder, detailType).replace(idPlaceholder, id);

        let current = true;

        setLoading(true);
        setState(null);
        setDetail(null);

        axios
            .get(url)
            .then(({ data }) => {
                // A second open landed while this was in flight; its own effect
                // owns the state now.
                if (!current) {
                    return;
                }

                setDetail(data?.data ?? null);
                setLoading(false);
            })
            .catch((error) => {
                if (!current) {
                    return;
                }

                setDetail(null);
                setState(stateFor(error));
                setLoading(false);
            });

        return () => {
            current = false;
        };
    }, [detailType, id]);

    return { detail, loading, state, gone: state === ACTION_GONE, reset, setDetail };
}
